import React from 'react';
import { Navigate } from 'react-router-dom';
import { BsFillArrowLeftCircleFill, BsFillGearFill } from 'react-icons/bs';
import { BiSolidUserX } from "react-icons/bi";
import { Server } from '../../server/server';
import MessageModal from '../modals/MessageModal';
import AlertModal from '../modals/AlertModal';
import QuestionModal from '../modals/QuestionModal';
import ContentDiv from '../elements/ContentDiv';
import PostPanel from '../elements/PostPanel';
import { getPortraitImage } from '../util/user';
import GroupChatModal from '../modals/GroupChatModal';
import './ChatPage.css'
import SitePage from './SitePage';
import Portrait from '../elements/Portrait';

interface ChatPageProps {
}

interface ChatPageState {
  id: string;
  loading: boolean;
  message: string;
  alert: string;
  question: string;
  scrollToBottom: boolean;
  unreadMessages: boolean;
  solo: boolean;
  showGroupModal: boolean;
  navigate: string;
  messages: any[];
  loadingMore: boolean;
  noMoreMessages: boolean;
}

class ChatPage extends React.Component<ChatPageProps, ChatPageState> {
  protected messagesDiv: React.RefObject<HTMLDivElement>;
  protected timer: any;
  protected processing: boolean;

  constructor(props:ChatPageProps) {
    super(props);
    this.processing = false;
    this.state = {
      id: '',
      loading: false,
      message: '',
      alert: '',
      question: '',
      scrollToBottom: false,
      unreadMessages: false,
      solo: false,
      showGroupModal: false,
      navigate: '',
      messages: [],
      loadingMore: false,
      noMoreMessages: false
    }

    this.messagesDiv = React.createRef();
    this.onChatMessageAdded = this.onChatMessageAdded.bind(this);
    this.onPageEvent = this.onPageEvent.bind(this);
    this.onChatLeft = this.onChatLeft.bind(this);
  }

  componentDidMount(): void {
    this.loadMessages();
    SitePage.addEventListener(this.onPageEvent);
    Server.monitor.addEventListener('chat-message-added', this.onChatMessageAdded);
    Server.network.addEventListener('chat-left', this.onChatLeft);
    this.timer = setInterval(()=>{
      this.checkScrollPosition();
    }, 100);
  }

  componentDidUpdate(prevProps: Readonly<ChatPageProps>, prevState: Readonly<ChatPageState>): void {
    if(this.messagesDiv.current) {
      if(prevState.id == this.state.id && this.state.scrollToBottom) {
        this.scrollToBottom();
        this.setState({scrollToBottom: false})
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
    Server.monitor.removeEventListener('chat-message-added', this.onChatMessageAdded);
    Server.network.removeEventListener('chat-left', this.onChatLeft);
    SitePage.removeEventListener(this.onPageEvent);
  }

  async getChatId() {
    let parts = window.location.pathname.split('/');
    if(parts.length < 3)
      return null;

    let chats = Server.user.getChats();

    let id = '';
    if(parts[2] == 'group')
      id = parts[3];
    else {
      let response = await Server.public.loadProfileFromSlug(parts[2]);
      if(response.success) {
        let chat = chats.find((chat:any) => chat.user == response.profile.id);
        if(!chat) 
            return null;
        id = chat.id;
      }
    }

    return id;
  }

  async setLastChatReadDate(id:string, messages:any[]) {
    if(messages.length > 0)
      await Server.monitor.setChatLastRead(id, messages[messages.length-1].date);
    else {
      let lastReadDate = Server.monitor.getChatLastRead(id);
      if(!lastReadDate)
        await Server.monitor.setChatLastRead(id, new Date().toISOString());
    }
  }

  async loadMessages() {
    let id = await this.getChatId();
    if(!id)
      return;

    this.processing = true;

    this.setState({id});

    let cachedMessages = await Server.chat.getMessagesCache(id);

    let updating = false;
    if(cachedMessages) {
      let profiles = cachedMessages.map((message:any) => message.user);
      profiles = profiles.filter((profile:any)=>Server.public.getProfile(profile) == null);
      if(profiles.length > 0) {
        this.setState({loading: true});
        await Server.public.loadProfiles(profiles);
      }

      let scrollToBottom = this.state.messages.length == 0;
      this.setState({messages: cachedMessages, loading: false, scrollToBottom});

      await this.setLastChatReadDate(id, cachedMessages);

      updating = cachedMessages.length > 0;
      let messageRefreshTime = window.sessionStorage.getItem(`ChatRefresh#${id}`);
      if(messageRefreshTime) {
        let refreshTime = parseInt(messageRefreshTime);
        let now = new Date().getTime();
        if(now - refreshTime < 1000 * 60) {
          this.processing = false;
          return;
        }
      }
    }

    let params:any = {limit: 25};

    if(updating)
      params.after = cachedMessages[cachedMessages.length-1].date;

    if(!updating)
      this.setState({loading: true});

    let response = await Server.chat.getChatMessages(id, params);

    this.setState({loading: false});

    if(!response.success) {
      this.processing = false;
      Server.chat.clearDatesCache();
      this.setState({alert: response.message, navigate: '/chats'});
      return;
    }

    window.sessionStorage.setItem(`ChatRefresh#${id}`, new Date().getTime().toString());
    
    let newMessages = response.messages;

    if(newMessages.length == 0 && updating) {
      this.processing = false;
      return;
    }

    if(!updating)
      this.setState({loading: true});

    for (let message of newMessages) 
      message.user = response.users[message.user];

    let profiles = this.getProfilesInMessages(newMessages);
    await Server.public.loadProfiles(profiles);

    let messages = [];
    if(updating)
      messages = [...cachedMessages];

    messages = this.addNewMessagesToExistingMessages(newMessages, messages);

    await Server.chat.setMessagesCache(id, messages, updating);

    let scrollToBottom = this.state.messages.length == 0 || this.isScrolledToBottom();

    this.processing = false;

    this.setState({messages, loading: false, scrollToBottom});

    await this.setLastChatReadDate(id, messages);
  }

  getProfilesInMessages(messages:any[]) {
    let profiles = [];
    for (let i = 0; i < messages.length; i++) {
      let user = messages[i].user;
      if(user && profiles.indexOf(user) == -1)
        profiles.push(user);
    }
    return profiles;
  }

  addNewMessagesToExistingMessages(newMessages:any[], existingMessages:any[]) {
    let messages = [...existingMessages];

    for (let newMessage of newMessages) {
      let existingIndex = messages.findIndex(message => 
        message.id === newMessage.id && message.date === newMessage.date
      );
      
      if (existingIndex !== -1) 
        messages[existingIndex] = newMessage;
      else
        messages.push(newMessage);
    }

    this.sortMessages(messages);

    return messages;
  }

  sortMessages(messages:any[]) {
    messages.sort((a:any, b:any)=>{
      if(a.date > b.date)
        return 1;
      else if(a.date < b.date)
        return -1;

      return 0;
    })
  }

  scrollToBottom() {
    if(this.messagesDiv.current)
      this.messagesDiv.current.scrollTop = this.messagesDiv.current.scrollHeight;
  }

  checkScrollPosition() {
    let div = this.messagesDiv.current;
    if(!div) return;

    let scrolledToBottom = (div.scrollTop == (div.scrollHeight - div.offsetHeight));

    if(scrolledToBottom && this.state.unreadMessages) 
      this.setState({unreadMessages: false});
  }

  isScrolledToBottom() {
    let div = this.messagesDiv.current;
    if(!div) return false;
    return (div.scrollTop == (div.scrollHeight - div.offsetHeight));
  }

  async onChatMessageAdded(event:any) {
    if(this.state.id != event.id || this.processing || this.state.loading || this.state.loadingMore)
      return;

    let messages = [...this.state.messages];
    messages.push({
      date: event.date,
      user: event.user,
      message: event.message,
      failed: false
    });

    await Server.chat.setMessagesCache(event.id, messages, true);
    await Server.monitor.setChatLastRead(event.id, event.date);

    let scrollToBottom = this.isScrolledToBottom();
    if(!scrollToBottom)
      this.setState({unreadMessages: true});

    this.setState({messages, scrollToBottom});
  }

  onPageEvent(event: any) {
    this.loadMessages();
  }

  onChatLeft(event:any) {
    if(event.chat == this.state.id && event.user == Server.user.getId()) {
      Server.user.leaveChat(this.state.id);
      Server.monitor.clearChatLastRead(this.state.id);
      Server.monitor.clearChatContent(this.state.id);
      Server.chat.clearDatesCache();
      this.setState({navigate: '/chats'});
    }
  }

  async onSend(content:string) {
    this.processing = true;

    let message = {
      date: new Date().toISOString(),
      user: Server.user.getId(),
      message: content,
      failed: false
    }

    let messages = [...this.state.messages];
    messages.push(message);
    this.setState({messages, scrollToBottom: true});

    let response = await Server.chat.sendChatMessage(this.state.id, content);

    if(!response.success) {
      message.failed = true;
      this.processing = false;
      this.forceUpdate();
      return;
    }

    message.date = response.date;
    this.forceUpdate();

    Server.chat.setMessagesCache(this.state.id, messages, true);
    await Server.monitor.setChatLastRead(this.state.id, response.date);

    Server.network.sendCommand('send-chat-message', {chat: this.state.id, date: response.date, message: content});

    this.processing = false;
  }

  onBack() {
    if(window.history.state && window.history.state.idx == 0)
      this.setState({navigate: '/chats'});
    else
      window.history.back();
  }

  onLeaveChat() {
    this.setState({question: 'Do you want to remove yourself from this chat?'})
  }

  async onQuestionYes() {
    this.setState({question: '', message: 'Leaving chat...'});

    setTimeout(async () => {
      let chat = Server.user.getChat(this.state.id);
      let response = await Server.chat.leaveChat({id: chat.id, type: chat.type});
      if(response.success) {
        Server.user.leaveChat(this.state.id);
        Server.monitor.clearChatLastRead(this.state.id);
        Server.monitor.clearChatContent(this.state.id);
        Server.chat.clearDatesCache();
        Server.network.sendCommand('leave-chat', {chat: this.state.id});
        this.onBack();
      }
      else
        this.setState({alert: response.message, message: ''});
    }, 500);
  }

  formatDate(str:string) {
    let date = new Date(str);
    let now = new Date();
    let yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
  
  
    let isToday = (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate()
    );
  
    let isYesterday = (
      date.getFullYear() === yesterday.getFullYear() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getDate() === yesterday.getDate()
    );
  
    let dateConfig:any = {month: "short", day: "numeric"};
    if(date.getFullYear() != now.getFullYear())
      dateConfig.year = "numeric";
  
    let dateStr = date.toLocaleString(undefined, dateConfig);
    let timeStr = date.toLocaleString(undefined, {hour: "numeric", minute: "numeric"});
  
    if(isToday)
      dateStr = 'Today';
    else if(isYesterday)
      dateStr = 'Yesterday';
  
    return dateStr + ' at ' + timeStr;
  }

  renderMessages() {
    let combinedMessages = [];
    for(let i = 0; i < this.state.messages.length; i++) {
      let entry = this.state.messages[i];
      let combined = false;

      if(combinedMessages.length > 0) {
        let lastCombinedMessage = combinedMessages[combinedMessages.length-1];
        if(lastCombinedMessage.user == entry.user && !entry.failed) {
          let thisDate = new Date(entry.date);
          let prevDate = new Date(lastCombinedMessage.date);
          let diff = (thisDate.getTime() - prevDate.getTime()) / 1000 / 60;
          if(diff < 10) {
            lastCombinedMessage.messages.push(entry.message);
            combined = true;
          }
        }
      }

      if(!combined) {
        combinedMessages.push({
          date: entry.date,
          user: entry.user,
          messages: [entry.message],
          failed: entry.failed ? true : false
        });
      }
    }

    let messages = [];
    for (let i = 0; i < combinedMessages.length; i++) {
      let msg = combinedMessages[i];
      let profile = Server.public.getProfile(msg.user);

      let timeDiv = null;
      if(msg.failed)
        timeDiv = <div style={{fontSize: '0.75em', color: 'var(--notification-background-color)'}}>FAILED TO SEND</div>
      else
        timeDiv = <div style={{fontSize: '0.75em', color: 'var(--panel-subtext-color)'}}>{this.formatDate(msg.date)}</div>

      let contentDivs = [];
      for(let j = 0; j < msg.messages.length; j++) 
        contentDivs.push(<ContentDiv key={j} content={msg.messages[j]} onImageLoad={()=>{this.setState({scrollToBottom: true})}} />)

      messages.push(
        <div key={i} className="site-page-row" style={{alignItems: 'flex-start'}}>
          <Portrait user={profile.id} size={0.9} />
          <div className="site-page-column" style={{rowGap: '1px'}}>
            <div className="site-page-row" style={{columnGap: '10px'}}>
              <div><b>{profile.name}</b></div>
              <div style={{marginTop: '2px'}}>{timeDiv}</div>
            </div>
            <div className="site-page-column" style={{rowGap: '0px'}}>
              {contentDivs}
            </div>
          </div>
        </div>
      )
    }

    return messages;
  }

  render() {
    if(!Server.account.isLoggedIn())
      return <Navigate to="/" replace={true} />

    if(this.state.navigate != '')
      return <Navigate to={this.state.navigate} />

    if(this.state.id == '')
      return null;

    let chat = Server.user.getChat(this.state.id);
    if(!chat)
      return null;

    let name = '';

    if(chat.type == 'direct') {
      let profile = Server.public.getProfile(chat.user);
      name = profile.name;
    }
    else if(chat.type == 'group')
      name = chat.name;

    return (
      <div className="chat-page">
        <div className="chat-page-panel">
          <div className="chat-page-content">
            <div className="chat-page-header">
              <div>
                {name}
              </div>
              <div className="chat-page-header-back" onClick={()=>this.onBack()} >
                <BsFillArrowLeftCircleFill size={22} />
              </div>
              {chat && chat.type == 'group' &&
                <div className="chat-page-header-settings" onClick={()=>this.setState({showGroupModal: true})}>
                  <BsFillGearFill size={22} />
                </div>
              }
              <div className="chat-page-header-leave" onClick={()=>this.onLeaveChat()}>
                <BiSolidUserX size={28} />
              </div>
              {this.state.unreadMessages &&
                <div className="chat-page-notification" onClick={()=>this.setState({scrollToBottom: true})}>
                  <div>New messages!</div>
                  <div>Jump to End</div>
                </div>
              }
              {!this.state.unreadMessages && this.state.solo && 
                <div className="chat-page-notification">
                  <div>Everyone has left the chat.</div>
                </div>
              }
              {!Server.account.isLoggedIn() &&
                <div className="chat-page-notification">
                  <div>Login to view the community chat.</div>
                </div>
              }
            </div>
            <div className="chat-page-messages" ref={this.messagesDiv}>
              {this.state.loading ? <div>Loading...</div> : this.renderMessages()}
            </div>
            <div className="chat-page-footer">
              <PostPanel enter={true} button="Send" onPost={(content:string)=>this.onSend(content)} maxLength={2048} />
            </div>
          </div>
        </div>
        {this.state.showGroupModal && 
          <GroupChatModal 
            title="Edit Group Chat"
            id={this.state.id}
            onClose={()=>this.setState({showGroupModal: false})}
          />
        }
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
        <MessageModal message={this.state.message} />
        <QuestionModal message={this.state.question} onYes={()=>this.onQuestionYes()} onNo={()=>this.setState({question: ''})} />
      </div>
    )
  }
}

export default ChatPage;
